import { ProductCategoryType } from '@bcube/shared';
import { Pencil, Trash2 } from 'lucide-react';
import type { PaginatedResponse } from '@/api/commercial/products';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { IProduct } from '@/types/commercial/product';

interface ProductTableProps {
  data: PaginatedResponse<IProduct>;
  currentPage: number;
  onPageChange: (page: number) => void;
  onEdit: (product: IProduct) => void;
  onDelete: (product: IProduct) => void;
  onActiveChange: (product: IProduct, active: boolean) => void;
  activeChangeDisabled?: boolean;
}

export function ProductTable({
  data,
  currentPage,
  onPageChange,
  onEdit,
  onDelete,
  onActiveChange,
  activeChangeDisabled = false,
}: ProductTableProps) {
  const formatDateTime = (value?: string | Date): string => {
    if (!value) return '-';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (cents?: number): string => {
    if (cents === undefined || cents === null) return '-';
    return (cents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const docs = data.docs ?? [];

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>SKU</TableHead>
              <TableHead>NOME</TableHead>
              <TableHead>DESCRIÇÃO</TableHead>
              <TableHead>CATEGORIA</TableHead>
              <TableHead>PREÇO</TableHead>
              <TableHead>DATA DE CRIAÇÃO</TableHead>
              <TableHead className="text-right">AÇÕES</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  Nenhum produto encontrado
                </TableCell>
              </TableRow>
            ) : (
              docs.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <span className="font-medium text-sm">{product.sku}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: product.color }}
                      />
                      <span className="font-medium">{product.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-sm whitespace-pre-wrap break-words">
                    {product.description?.trim() || '—'}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm flex items-center gap-1.5">
                      {product.category?.name || '-'}
                      {product.category?.type === ProductCategoryType.SYSTEM && (
                        <Badge variant="info-subtle" className="text-[10px] px-1.5 py-0">
                          Sistema
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatPrice(product.priceInCents)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDateTime(product.created_at)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(product)}
                        aria-label={`Editar produto ${product.name}`}
                        title="Editar produto"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(product)}
                        disabled={product.hasUsage !== false}
                        aria-label={`Excluir produto ${product.name}`}
                        title={
                          product.hasUsage
                            ? 'Produto já utilizado. Utilize a opção de inativar.'
                            : 'Excluir produto'
                        }
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                      <Switch
                        checked={product.active}
                        onCheckedChange={(checked) => onActiveChange(product, checked)}
                        disabled={activeChangeDisabled}
                        aria-label={`${product.active ? 'Inativar' : 'Ativar'} produto ${product.name}`}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={data.totalPages}
        totalItems={data.total}
        limit={data.limit}
        onPageChange={onPageChange}
      />
    </div>
  );
}
